import React, { useState, useContext, useRef } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalError from "../../components/NtModalError";
import NtModal from "../../components/NtModal";
import NtModalConirmAlert from "../../components/NtModalConfirmAlert";
import NtModalAccountPickerField from "./NtModalAccountPickerField";
import { useTheme } from "../../context/ThemeContext";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import {
  createIvrOption,
  updateIvrOption,
  deleteIvrOption,
} from "../../api/ivr";

/**
 * IVR option editor.
 *
 * `draft` mode: when the option is being authored before its parent menu
 * exists (inside the new-menu modal), the form skips the API call and just
 * hands the params back to the parent via `onSave`. The parent batches the
 * options and POSTs them after the menu is created.
 */
function NtIvrOptionForm({
  menuId,
  item,
  onCancel,
  onSave,
  onDelete,
  draft = false,
}) {
  const context = useContext(UserContext);
  const { theme } = useTheme();
  const isEdit = !!item;
  const domain = context.user?.domain;
  const confirmRef = useRef();

  const [digits, setDigits] = useState(item?.digits || "");
  const [description, setDescription] = useState(item?.description || "");
  const [destination, setDestination] = useState(
    item?.action
      ? { name: item.action.name, value: item.action.value }
      : null
  );
  const [order, setOrder] = useState(String(item?.order || "0"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // hangup/top-menu/back are valid actions that intentionally carry a null
  // value (backend ivrActionSchema enforces this), so only require a value
  // for actions that actually point at a target.
  const valuelessActions = ["hangup", "top-menu", "back"];

  const isRequiredFields = () => {
    if (!digits || !destination?.name) return false;
    if (valuelessActions.includes(destination.name)) return true;
    return !!destination.value;
  };

  const handleSave = async () => {
    setError(null);

    const params = {
      ivr_menu_option_digits: digits,
      ivr_menu_option_description: description || undefined,
      action: { name: destination?.name, value: destination?.value },
      ivr_menu_option_order: order ? parseInt(order, 10) : undefined,
    };

    // Draft mode (new menu modal): no API call, just hand back the params.
    if (draft) {
      onSave?.(params);
      return;
    }

    setLoading(true);

    const response = isEdit
      ? await updateIvrOption(
          domain,
          menuId,
          item.id || item.ivr_menu_option_uuid,
          params
        )
      : await createIvrOption(domain, menuId, params);

    Logger("IVR Option Save", response);

    if (response.ok) {
      if (onSave) {
        onSave(response.data);
      }
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);

    setError(null);

    const response = await deleteIvrOption(
      domain,
      menuId,
      item.id || item.ivr_menu_option_uuid
    );

    Logger("IVR Option Delete", response);

    if (response.ok) {
      if (onDelete) {
        onDelete();
      }
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  const renderConfirmModal = () => {
    return (
      <NtModal ref={confirmRef}>
        <NtModalConirmAlert
          title={"Delete"}
          message={
            "Are you sure you want to delete this option? This action is permanent and cannot be undone."
          }
          onCancel={() => {
            confirmRef.current?.dismiss();
          }}
          onAccept={() => {
            confirmRef.current?.dismiss();
            handleDelete();
          }}
        />
      </NtModal>
    );
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>{isEdit ? "Update Option" : "Add new Option"}</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        Map a keypad digit to an action like transfer, hangup, or playback.
      </NtModalSubtitle>

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 25 }}
          title={"Digit *"}
          placeholder={"e.g. 1"}
          value={digits}
          setValue={(text) => setDigits(text)}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Description"}
          placeholder={"Description"}
          value={description}
          setValue={(text) => setDescription(text)}
        />

        <NtModalAccountPickerField
          containerStyle={{ marginTop: 15 }}
          title={"Destination *"}
          placeholder={"Select destination"}
          value={destination}
          setValue={(value) => setDestination(value)}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Order"}
          placeholder={"0"}
          value={order}
          setValue={(text) => setOrder(text)}
        />

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 25,
        }}
      >
        {isEdit ? (
          <NtModalActiveButton
            containerStyle={{ backgroundColor: theme.red }}
            title={"Delete"}
            icon={"delete"}
            onPress={() => {
              confirmRef.current?.show();
            }}
            enabled={true}
          />
        ) : (
          <View />
        )}

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}

          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />

          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={isEdit ? "Update" : "Create"}
            onPress={() => {
              if (isRequiredFields()) {
                handleSave();
              }
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>
      {renderConfirmModal()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtIvrOptionForm;
